import { useEffect, useState } from 'react';

const IN_APP_RE = /FBAN|FBAV|FB_IAB|FB4A|Instagram|WhatsApp|Telegram|TelegramBot|Line\/|Snapchat|TikTok|musical_ly|LinkedInApp|Twitter|X-Android|GSA\/|; wv\)/i;

const inAppName = (ua) => {
  if (/WhatsApp/i.test(ua)) return 'WhatsApp';
  if (/FBAN|FBAV|FB_IAB|FB4A/i.test(ua)) return 'Facebook';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/Telegram/i.test(ua)) return 'Telegram';
  if (/TikTok|musical_ly/i.test(ua)) return 'TikTok';
  if (/Snapchat/i.test(ua)) return 'Snapchat';
  if (/LinkedInApp/i.test(ua)) return 'LinkedIn';
  if (/GSA\//i.test(ua)) return 'Google';
  return 'une application';
};

const APP_PACKAGES = { 'com.whatsapp': 'WhatsApp', 'com.whatsapp.w4b': 'WhatsApp Business', 'com.facebook.katana': 'Facebook', 'com.facebook.orca': 'Messenger', 'com.facebook.lite': 'Facebook Lite', 'com.instagram.android': 'Instagram', 'org.telegram.messenger': 'Telegram', 'com.google.android.gm': 'Gmail', 'com.snapchat.android': 'Snapchat', 'com.twitter.android': 'X', 'com.zhiliaoapp.musically': 'TikTok', 'com.linkedin.android': 'LinkedIn', 'com.google.android.googlequicksearchbox': 'Google' };

// Chrome Custom Tabs (links opened from WhatsApp/Facebook…) look like Chrome but never fire beforeinstallprompt.
// Chrome exposes the opener via document.referrer = android-app://<package>/
function openedFromApp() {
  try {
    const saved = sessionStorage.getItem('mboa_opened_from_app');
    if (saved) return saved;
    const ref = document.referrer || '';
    if (ref.startsWith('android-app://')) {
      const pkg = ref.replace('android-app://', '').split('/')[0];
      if (pkg && pkg !== 'com.android.chrome') {
        const name = APP_PACKAGES[pkg] || 'une application';
        sessionStorage.setItem('mboa_opened_from_app', name);
        return name;
      }
    }
  } catch {}
  return '';
}

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

export function detectInstallContext() {
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const fromApp = isAndroid ? openedFromApp() : '';
  const inApp = IN_APP_RE.test(ua) || !!fromApp;
  const samsung = /SamsungBrowser/i.test(ua);
  const firefox = /Firefox|FxiOS/i.test(ua);
  const opera = /OPR\/|Opera|OPT\//i.test(ua);
  const edge = /EdgA|EdgiOS|Edg\//i.test(ua);
  const chrome = /Chrome\/|CriOS/i.test(ua) && !samsung && !opera && !edge;
  const safari = isIOS && /Safari/i.test(ua) && !chrome && !firefox && !edge && !opera;

  let browser = 'other';
  if (inApp) browser = 'inapp';
  else if (samsung) browser = 'samsung';
  else if (edge) browser = 'edge';
  else if (opera) browser = 'opera';
  else if (firefox) browser = 'firefox';
  else if (chrome) browser = 'chrome';
  else if (safari) browser = 'safari';

  const labels = { inapp: fromApp || inAppName(ua), samsung: 'Samsung Internet', edge: 'Microsoft Edge', opera: 'Opera', firefox: 'Firefox', chrome: 'Chrome', safari: 'Safari', other: 'Navigateur inconnu' };

  return {
    ua, isAndroid, isIOS,
    isMobile: isAndroid || isIOS,
    standalone: isStandalone(),
    browser,
    browserLabel: labels[browser],
    inAppName: inApp ? (fromApp || inAppName(ua)) : '',
    customTab: !!fromApp,
    canPrompt: !!window.__mboaInstallPrompt,
    installedBefore: !!localStorage.getItem('mboa_pwa_installed'),
    hasServiceWorker: 'serviceWorker' in navigator,
    isSecure: window.isSecureContext,
  };
}

export function useInstallContext() {
  const [ctx, setCtx] = useState(detectInstallContext);
  useEffect(() => {
    const refresh = () => setCtx(detectInstallContext());
    window.addEventListener('mboa-install-ready', refresh);
    window.addEventListener('mboa-app-installed', refresh);
    return () => { window.removeEventListener('mboa-install-ready', refresh); window.removeEventListener('mboa-app-installed', refresh); };
  }, []);
  return ctx;
}

export function waitForInstallPrompt(ms = 2500) {
  if (window.__mboaInstallPrompt) return Promise.resolve(true);
  return new Promise((resolve) => {
    const t = setTimeout(() => { window.removeEventListener('mboa-install-ready', ok); resolve(false); }, ms);
    const ok = () => { clearTimeout(t); window.removeEventListener('mboa-install-ready', ok); resolve(!!window.__mboaInstallPrompt); };
    window.addEventListener('mboa-install-ready', ok);
  });
}

export async function triggerNativeInstall() {
  const p = window.__mboaInstallPrompt;
  if (!p) return 'unavailable';
  trackInstall('prompt_shown');
  p.prompt();
  const { outcome } = await p.userChoice;
  trackInstall(outcome === 'accepted' ? 'prompt_accepted' : 'prompt_dismissed');
  window.__mboaInstallPrompt = null;
  window.dispatchEvent(new Event('mboa-install-ready'));
  return outcome;
}

export function openInChrome(path) {
  trackInstall('open_chrome_click');
  const target = path || `${window.location.pathname}${window.location.search}`;
  const host = window.location.host;
  const fallback = encodeURIComponent(`${window.location.origin}/installer?from=intent`);
  window.location.href = `intent://${host}${target}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
}

export async function copyCurrentLink() {
  const url = `${window.location.origin}${window.location.pathname}`;
  try { await navigator.clipboard.writeText(url); return true; } catch { return false; }
}

const sessionId = () => {
  try {
    let s = sessionStorage.getItem('mboa_pwa_sid');
    if (!s) { s = Math.random().toString(36).slice(2, 12); sessionStorage.setItem('mboa_pwa_sid', s); }
    return s;
  } catch { return ''; }
};

export function trackInstall(event, ctx) {
  try {
    const c = ctx || detectInstallContext();
    const body = JSON.stringify({ event, browser: c.browser, platform: c.isAndroid ? 'android' : c.isIOS ? 'ios' : 'desktop', in_app: c.inAppName || '', standalone: c.standalone, path: window.location.pathname, session: sessionId() });
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pwa/events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch {}
}

if (typeof window !== 'undefined') {
  window.addEventListener('mboa-app-installed', () => trackInstall('installed'));
}
